import { HttpErrorResponse } from '@angular/common/http';
import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { ChangeDetectorRef } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { EMPTY } from 'rxjs';
import { BehaviorSubject } from 'rxjs';
import {
  catchError,
  filter,
  finalize,
  map,
  switchMap,
  takeUntil,
  tap,
} from 'rxjs/operators';

import { AdminBackend } from '@libs/src/admin/admin.backend';
import { DestroySubscriptionsService } from '@libs/src/destroy-subscription/destroy-subscription.service';
import { GroupsStoreService } from '@libs/src/groups/groups-store/groups-store.service';
import { ToastService } from '@libs/src/toast/toast.service';
import { ZendeskService } from '@libs/src/zendesk/zendesk.service';

import { getServerError } from '@libs/src/error-handler/error-handler.helper';
import { Group } from '@libs/src/models/group.model';
import { UploadCSVUniqueColumn } from '@libs/src/utilities/csv.utilities';
import { IAppState } from '@main-client/src/app/app.state';
import { UploadFileEvent } from '@main-client/src/app/group-edit-members/group-edit-members.interface';

import { ActionQueueService } from '../action-queue/action-queue.service';

type ViewMode =
  | 'action_items_queues'
  | 'attribute_mapping'
  | 'import_file'
  | 'upload_completed'
  | 'upload_failed'
  | 'upload_file';

const viewModes: { [key in ViewMode]: ViewMode } = {
  action_items_queues: 'action_items_queues',
  attribute_mapping: 'attribute_mapping',
  import_file: 'import_file',
  upload_completed: 'upload_completed',
  upload_failed: 'upload_failed',
  upload_file: 'upload_file',
};

@Component({
  providers: [DestroySubscriptionsService],
  selector: 'app-members-csv-upload',
  templateUrl: './csv-upload.component.html',
})
export class CSVUploadViewComponent implements OnInit {
  @ViewChild('fileInput') fileInput: ElementRef;
  file: File;
  isLoading = false;
  viewMode = viewModes.import_file;
  viewModes = viewModes;
  groups: Group[] = [];
  selectedGroup: Group;
  selectedGroup$: BehaviorSubject<Group> = new BehaviorSubject<Group>(null);
  parsedData: UploadCSVUniqueColumn[];
  uploadedFlatFileName: string;
  uploadedFlatFileGeneration: string;
  associatedWithActionQueueGroupData: {
    groupId: Group['_id'];
    groupName: Group['name'];
  };
  automatedUserManagementBannerText: {
    buttonText: string;
    description: string;
    imageName: string;
    title: string;
  };
  user = this.store.select('user');
  tenant = this.store.select('tenant');

  constructor(
    private readonly actionQueueService: ActionQueueService,
    private readonly adminBackend: AdminBackend,
    private readonly cdr: ChangeDetectorRef,
    private readonly destroy$: DestroySubscriptionsService,
    private readonly groupsStoreService: GroupsStoreService,
    public readonly router: Router,
    private readonly store: Store<IAppState>,
    private readonly toastService: ToastService,
    private readonly zendesk: ZendeskService,
  ) {}

  ngOnInit(): void {
    this.adminBackend
      .getGroups({ limit: '500' })
      .pipe(
        catchError((response: HttpErrorResponse) => {
          this.handleError(response);
          return EMPTY;
        }),
        map(({ groups }: { count: number; groups: Group[] }) => {
          this.groups = groups;
          if (1 === this.groups.length) {
            this.selectedGroup = this.groups[0];
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
    this.actionQueueService
      .handleActiveMembershipFileUpload()
      .pipe(
        filter((activeMembershipFileUpload) => !!activeMembershipFileUpload),
        switchMap(
          (activeMembershipFileUpload: {
            fileName: string;
            groupId: Group['_id'];
          }) =>
            this.groupsStoreService
              .getGroupFromStore({ _id: activeMembershipFileUpload.groupId })
              .pipe(
                map((group) => ({
                  groupId: group.document._id,
                  groupName: group.document.name,
                  uploadedFlatFileName: activeMembershipFileUpload.fileName,
                })),
                tap(({ groupId, groupName, uploadedFlatFileName }) => {
                  this.associatedWithActionQueueGroupData = {
                    groupId,
                    groupName,
                  };
                  this.uploadedFlatFileName = uploadedFlatFileName;
                  this.automatedUserManagementBannerText = {
                    buttonText: $localize`:@@global.reviewMemberUpdates:Review member updates`,
                    description: $localize`:@@membersView.aumBanner.description:Review and approve member updates from ${uploadedFlatFileName}.`,
                    imageName: 'upload_file',
                    title: $localize`:@@membersView.aumBanner.title:A new file has been uploaded to ${this.associatedWithActionQueueGroupData.groupName}`,
                  };
                }),
              ),
        ),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  createFormData(): FormData {
    const formData = new FormData();
    formData.append('file', this.file);
    formData.append('slug', this.selectedGroup?.slug);
    return formData;
  }

  uploadCsvFile(viewMode: string) {
    if (viewModes.upload_failed === viewMode) {
      this.setViewModeAndLoadingStatus(viewMode, false);
      return;
    }
    this.viewMode = viewModes.upload_file;
    this.isLoading = true;
    const formData = this.createFormData();
    this.adminBackend.v3
      .postUpload(formData)
      .pipe(
        tap((response) => {
          this.associatedWithActionQueueGroupData = {
            groupId: this.selectedGroup._id,
            groupName: this.selectedGroup.name,
          };
          this.uploadedFlatFileName = this.file.name;
          this.uploadedFlatFileGeneration = response.flat_file_generation;
          this.navigateToActionQueues();
        }),
        catchError((response: HttpErrorResponse) => {
          this.setViewModeAndLoadingStatus(viewModes.upload_failed, false);
          this.handleError(response);
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  onGroupChange(group: Group) {
    this.selectedGroup =
      this.groups.find(({ slug }) => slug === group?.slug) || null;
    if (!this.selectedGroup) {
      return;
    }
    this.selectedGroup$.next(this.selectedGroup);
  }

  parseCsvFile() {
    this.setViewModeAndLoadingStatus(viewModes.upload_file, true);
    const formData = this.createFormData();
    this.adminBackend.v3
      .postParseCsv(formData)
      .pipe(
        catchError((response: HttpErrorResponse) => {
          this.setViewModeAndLoadingStatus(viewModes.upload_failed, false);
          this.handleError(response);
          return EMPTY;
        }),
        tap(
          (response: {
            data: UploadCSVUniqueColumn[];
            originalName: string;
          }) => {
            this.setViewModeAndLoadingStatus(viewModes.upload_completed, false);
            this.selectedGroup$.next(this.selectedGroup);
            this.parsedData = response.data;
            this.viewMode = viewModes.attribute_mapping;
            this.cdr.markForCheck();
          },
        ),
        finalize(() => this.setViewModeAndLoadingStatus(this.viewMode, false)),
        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  getCurrentStepIndex() {
    if (
      this.viewModes.upload_file === this.viewMode ||
      this.viewModes.import_file === this.viewMode
    ) {
      return 0;
    }
    if (this.viewModes.attribute_mapping === this.viewMode) {
      return 1;
    }
    return 2;
  }

  setViewModeAndLoadingStatus(viewMode: ViewMode, isLoading: boolean) {
    this.viewMode = viewMode;
    this.isLoading = isLoading;
  }

  openFileInput() {
    this.fileInput.nativeElement.click();
  }

  restartUpload() {
    this.viewMode = viewModes.import_file;
  }

  helpCenterClicked() {
    this.zendesk.showZendesk();
  }

  onAfterAddingFile(uploadEvent?: Event & UploadFileEvent) {
    const csvFile = uploadEvent?.target?.files[0];
    if (!csvFile) {
      return;
    }
    this.file = csvFile;
  }

  removeUploadedFile() {
    this.file = null;
    this.fileInput.nativeElement.value = null;
  }

  handleError(response: HttpErrorResponse) {
    const errorMessage = getServerError(response);
    this.toastService.showError(errorMessage);
  }

  navigateToActionQueues() {
    this.router.navigate(['admin/members/import-membership-file/review'], {
      queryParams: {
        fileName: this.uploadedFlatFileName,
        flatFileGeneration: this.uploadedFlatFileGeneration,
        groupId: this.associatedWithActionQueueGroupData.groupId,
        groupName: this.associatedWithActionQueueGroupData.groupName,
      },
    });
  }
}
